import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import XPProgressHeader from '../../components/ui/XPProgressHeader';
import AIChatbot from '../../components/ui/AIChatbot';
import ProgressOverview from './components/ProgressOverview';
import CategorySection from './components/CategorySection';
import XPGainNotification from './components/XPGainNotification';
import QuickStats from './components/QuickStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const StartHereHub = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [currentXP, setCurrentXP] = useState(0);
  const [recentXPGain, setRecentXPGain] = useState(0);
  const [showXPNotification, setShowXPNotification] = useState(false);
  const [notificationXP, setNotificationXP] = useState(0);

  const categories = [
    {
      name: 'Account Setup',
      icon: 'UserCog',
      iconColor: 'var(--color-primary)',
      bgColor: 'bg-primary/10'
    },
    {
      name: 'Team Introductions',
      icon: 'Users',
      iconColor: 'var(--color-accent)',
      bgColor: 'bg-accent/10'
    },
    {
      name: 'System Access',
      icon: 'Key',
      iconColor: 'var(--color-success)',
      bgColor: 'bg-success/10'
    },
    {
      name: 'Learning & Development',
      icon: 'BookOpen',
      iconColor: 'var(--color-warning)',
      bgColor: 'bg-warning/10'
    }
  ];

  const tasks = [
    {
      id: 1,
      title: 'Complete Your Profile',
      description: 'Add your photo, bio, and contact information to help teammates get to know you better.',
      category: 'Account Setup',
      priority: 'high',
      xp: 50,
      estimatedTime: '10 min',
      resources: [
        { title: 'Profile Setup Guide', url: '#' },
        { title: 'Photo Guidelines', url: '#' }
      ]
    },
    {
      id: 2,
      title: 'Set Up Two-Factor Authentication',
      description: 'Secure your account by enabling 2FA for enhanced security protection.',
      category: 'Account Setup',
      priority: 'high',
      xp: 75,
      estimatedTime: '5 min',
      resources: [
        { title: '2FA Setup Instructions', url: '#' }
      ]
    },
    {
      id: 3,
      title: 'Configure Email Preferences',
      description: 'Choose which notifications you want to receive and set your email preferences.',
      category: 'Account Setup',
      priority: 'medium',
      xp: 25,
      estimatedTime: '5 min',
      resources: []
    },
    {
      id: 4,
      title: 'Meet Your Team Lead',
      description: 'Schedule a 30-minute introduction call with your direct manager to discuss goals and expectations.',
      category: 'Team Introductions',
      priority: 'high',
      xp: 100,
      estimatedTime: '30 min',
      resources: [
        { title: 'Calendar Booking Link', url: '#' },
        { title: 'Meeting Agenda Template', url: '#' }
      ]
    },
    {
      id: 5,
      title: 'Join Team Slack Channels',
      description: 'Connect with your team by joining relevant Slack channels for communication and collaboration.',
      category: 'Team Introductions',
      priority: 'high',
      xp: 50,
      estimatedTime: '10 min',
      resources: [
        { title: 'Channel Directory', url: '#' }
      ]
    },
    {
      id: 6,
      title: 'Introduce Yourself in #general',
      description: 'Post a brief introduction about yourself, your role, and what you\'re excited about in the #general channel.',
      category: 'Team Introductions',
      priority: 'medium',
      xp: 75,
      estimatedTime: '15 min',
      resources: [
        { title: 'Introduction Template', url: '#' }
      ]
    },
    {
      id: 7,
      title: 'Access Development Environment',
      description: 'Set up your local development environment with necessary tools, credentials, and repository access.',
      category: 'System Access',
      priority: 'high',
      xp: 150,
      estimatedTime: '45 min',
      resources: [
        { title: 'Dev Environment Guide', url: '#' },
        { title: 'Repository Access Request', url: '#' },
        { title: 'VPN Setup Instructions', url: '#' }
      ]
    },
    {
      id: 8,
      title: 'Configure Project Management Tools',
      description: 'Get access to Jira, Confluence, and other project management platforms used by your team.',
      category: 'System Access',
      priority: 'high',
      xp: 50,
      estimatedTime: '15 min',
      resources: [
        { title: 'Tool Access Request Form', url: '#' }
      ]
    },
    {
      id: 9,
      title: 'Set Up VPN Access',
      description: 'Install and configure VPN client for secure remote access to company resources.',
      category: 'System Access',
      priority: 'medium',
      xp: 50,
      estimatedTime: '20 min',
      resources: [
        { title: 'VPN Installation Guide', url: '#' },
        { title: 'Troubleshooting FAQ', url: '#' }
      ]
    },
    {
      id: 10,
      title: 'Complete Security Training',
      description: 'Watch mandatory security awareness training videos and pass the certification quiz.',
      category: 'Learning & Development',
      priority: 'high',
      xp: 100,
      estimatedTime: '60 min',
      resources: [
        { title: 'Training Portal', url: '#' },
        { title: 'Security Policies', url: '#' }
      ]
    },
    {
      id: 11,
      title: 'Review Company Handbook',
      description: 'Read through the employee handbook to understand company policies, benefits, and culture.',
      category: 'Learning & Development',
      priority: 'medium',
      xp: 75,
      estimatedTime: '45 min',
      resources: [
        { title: 'Employee Handbook PDF', url: '#' }
      ]
    },
    {
      id: 12,
      title: 'Watch Product Demo Videos',
      description: 'Familiarize yourself with our products by watching comprehensive demo and tutorial videos.',
      category: 'Learning & Development',
      priority: 'low',
      xp: 50,
      estimatedTime: '30 min',
      resources: [
        { title: 'Product Demo Playlist', url: '#' },
        { title: 'Feature Documentation', url: '#' }
      ]
    }
  ];

  const totalXP = tasks?.reduce((sum, task) => sum + task?.xp, 0);
  const totalTasks = tasks?.length;
  const completedTasks = completedTaskIds?.length;

  const quickStats = [
    {
      icon: 'CheckCircle2',
      iconColor: 'var(--color-success)',
      bgColor: 'bg-success/10',
      value: completedTasks,
      label: 'Tasks Completed',
      trend: 'up',
      trendValue: '+' + completedTasks,
      trendColor: 'text-success'
    },
    {
      icon: 'Clock',
      iconColor: 'var(--color-warning)',
      bgColor: 'bg-warning/10',
      value: totalTasks - completedTasks,
      label: 'Tasks Remaining',
      trend: completedTasks > 0 ? 'down' : null,
      trendValue: completedTasks > 0 ? '-' + completedTasks : null,
      trendColor: 'text-warning'
    },
    {
      icon: 'Zap',
      iconColor: 'var(--color-primary)',
      bgColor: 'bg-primary/10',
      value: currentXP,
      label: 'Total XP Earned',
      trend: recentXPGain > 0 ? 'up' : null,
      trendValue: recentXPGain > 0 ? '+' + recentXPGain : null,
      trendColor: 'text-primary'
    },
    {
      icon: 'Target',
      iconColor: 'var(--color-accent)',
      bgColor: 'bg-accent/10',
      value: Math.round((completedTasks / totalTasks) * 100) + '%',
      label: 'Overall Progress',
      trend: completedTasks > 0 ? 'up' : null,
      trendValue: completedTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) + '%' : null,
      trendColor: 'text-accent'
    }
  ];

  const handleTaskComplete = (taskId, xpGained) => {
    if (!completedTaskIds?.includes(taskId)) {
      setCompletedTaskIds([...completedTaskIds, taskId]);
      setCurrentXP(currentXP + xpGained);
      setRecentXPGain(xpGained);
      setNotificationXP(xpGained);
      setShowXPNotification(true);
    }
  };

  const handleNotificationComplete = () => {
    setShowXPNotification(false);
  };

  const getCurrentLevel = () => {
    if (currentXP >= 800) return 'Colleague';
    if (currentXP >= 500) return 'Contributor';
    if (currentXP >= 200) return 'Explorer';
    return 'Newbie';
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <XPProgressHeader
        currentXP={currentXP}
        level={getCurrentLevel()}
        totalXP={totalXP}
        recentGain={recentXPGain}
      />
      <main className={`pt-20 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <div className="p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Start Here Hub</h1>
              <p className="text-muted-foreground">
                Complete your onboarding tasks and earn XP to level up your journey
              </p>
            </div>
            <Button variant="outline" iconName="RotateCcw" iconPosition="left">
              Reset Progress
            </Button>
          </div>

          <QuickStats stats={quickStats} />

          <ProgressOverview
            totalTasks={totalTasks}
            completedTasks={completedTasks}
            totalXP={totalXP}
            earnedXP={currentXP}
          />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Icon name="ListTodo" size={24} color="var(--color-primary)" strokeWidth={2.5} />
                Onboarding Checklist
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" iconName="Filter">
                  Filter
                </Button>
                <Button variant="ghost" size="sm" iconName="SortAsc">
                  Sort
                </Button>
              </div>
            </div>

            {categories?.map((category) => (
              <CategorySection
                key={category?.name}
                category={category}
                tasks={tasks}
                onTaskComplete={handleTaskComplete}
                completedTaskIds={completedTaskIds}
              />
            ))}
          </div>

          {completedTasks === totalTasks && (
            <div className="bg-gradient-to-r from-success/20 to-primary/20 border-2 border-success/30 rounded-lg p-6 text-center animate-scale-in">
              <div className="w-16 h-16 bg-success/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Trophy" size={32} color="var(--color-success)" strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Congratulations!</h3>
              <p className="text-muted-foreground mb-4">
                You've completed all onboarding tasks and earned {totalXP} XP!
              </p>
              <Button variant="default" iconName="ArrowRight" iconPosition="right">
                Explore More Hubs
              </Button>
            </div>
          )}
        </div>
      </main>
      <AIChatbot />
      {showXPNotification && (
        <XPGainNotification xpGained={notificationXP} onComplete={handleNotificationComplete} />
      )}
    </div>
  );
};

export default StartHereHub;